import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { ListResponse, Subject, SubjectFacePreview } from '../../app/types';
import { fileToBase64 } from '../../lib/files';
import PageSection from '../shared/PageSection';

interface WatchlistPageProps {
  readonly tenantId: string;
  readonly subjects: Subject[];
  readonly onCreateSubject: (payload: { tenantId: string; displayName: string; notes?: string }) => Promise<Subject>;
  readonly onAddFace: (subjectId: string, payload: { tenantId: string; sourceImageName: string; imageBase64: string }) => Promise<void>;
  readonly onGetSubjectFaces: (subjectId: string) => Promise<ListResponse<SubjectFacePreview>>;
}

const MAX_IMAGE_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);

export default function WatchlistPage({
  tenantId,
  subjects,
  onCreateSubject,
  onAddFace,
  onGetSubjectFaces,
}: WatchlistPageProps) {
  const [displayName, setDisplayName] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [uploadingSubjectId, setUploadingSubjectId] = useState('');
  const [previewSubjectId, setPreviewSubjectId] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewFaces, setPreviewFaces] = useState<SubjectFacePreview[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const create = async () => {
    try {
      setError('');
      await onCreateSubject({ tenantId, displayName, notes: notes || undefined });
      setDisplayName('');
      setNotes('');
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Unable to create subject.');
    }
  };

  const uploadFace = async (subjectId: string, files: FileList | null) => {
    const file = files?.[0];
    if (!file) {
      return;
    }
    if (file.size > MAX_IMAGE_FILE_BYTES) {
      setError('Image is too large. Maximum supported size is 5 MB.');
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      setError('Unsupported image format. Please upload a JPEG or PNG image.');
      return;
    }

    try {
      setUploadingSubjectId(subjectId);
      setError('');
      const imageBase64 = await fileToBase64(file);
      await onAddFace(subjectId, {
        tenantId,
        sourceImageName: file.name,
        imageBase64,
      });
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Unable to upload subject face.');
    } finally {
      setUploadingSubjectId('');
    }
  };

  const openFacePreview = async (subject: Subject) => {
    setPreviewSubjectId(subject.subjectId);
    setPreviewTitle(`${subject.displayName} (${subject.subjectId.slice(0, 8)})`);
    setPreviewError('');
    setPreviewFaces([]);
    setPreviewLoading(true);

    try {
      const response = await onGetSubjectFaces(subject.subjectId);
      setPreviewFaces(response.items);
    } catch (previewError) {
      setPreviewError(previewError instanceof Error ? previewError.message : 'Unable to load subject faces.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeFacePreview = () => {
    setPreviewSubjectId('');
    setPreviewTitle('');
    setPreviewFaces([]);
    setPreviewError('');
  };

  return (
    <>
      <Stack spacing={3}>
      <PageSection title="Watchlist Subjects" subtitle="Known persons indexed into the tenant collection strategy.">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Subject</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Face</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subjects.map((subject) => (
              <TableRow key={subject.subjectId}>
                <TableCell>
                  {subject.displayName}
                  <br />
                  <small>{subject.subjectId}</small>
                </TableCell>
                <TableCell>{subject.notes || 'No notes'}</TableCell>
                <TableCell>{new Date(subject.createdAt).toLocaleString()}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button component="label" variant="outlined" size="small" disabled={uploadingSubjectId === subject.subjectId}>
                      {uploadingSubjectId === subject.subjectId ? 'Uploading...' : 'Add Face'}
                      <input
                        hidden
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={(event) => void uploadFace(subject.subjectId, event.target.files)}
                      />
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => void openFacePreview(subject)}
                      disabled={previewLoading && previewSubjectId === subject.subjectId}
                    >
                      {previewLoading && previewSubjectId === subject.subjectId ? 'Loading...' : 'View Faces'}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PageSection>
      <PageSection title="Create Subject">
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField label="Display Name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
          <TextField label="Notes" multiline minRows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
          <Button variant="contained" onClick={() => void create()} disabled={!displayName}>
            Create Subject
          </Button>
        </Stack>
      </PageSection>
    </Stack>
    <Dialog open={Boolean(previewSubjectId)} onClose={closeFacePreview} maxWidth="md" fullWidth>
      <DialogTitle>{previewTitle || 'Subject Faces'}</DialogTitle>
      <DialogContent>
        {previewLoading ? (
          <Typography color="text.secondary">Loading faces...</Typography>
        ) : previewError ? (
          <Alert severity="error">{previewError}</Alert>
        ) : !previewFaces.length ? (
          <Typography color="text.secondary">No faces have been uploaded for this subject yet.</Typography>
        ) : (
          <Grid container spacing={2}>
            {previewFaces.map((face) => (
              <Grid item xs={12} sm={6} md={4} key={face.faceId}>
                {face.previewUrl ? (
                  <img
                    src={face.previewUrl}
                    alt="Watchlist face"
                    style={{ width: '100%', height: 'auto', borderRadius: 8, display: 'block' }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: 180,
                      border: '1px dashed',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography color="text.secondary">Preview unavailable</Typography>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                  Uploaded {new Date(face.createdAt).toLocaleString()}
                </Typography>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
